import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { AggregatedMoney, AggregatedSummary } from '../../../api/types/analytics';
import { percentDelta } from '../../../api/analyticsAggregate';
import { formatInt, formatMoney, formatMonth, formatPercent } from './format';
import {
  Delta,
  InfoDot,
  KpiCard,
  KpiGrid,
  KpiLabel,
  KpiSecondary,
  KpiValue,
  Card,
  Empty,
} from './styles';

type Props = {
  summary: AggregatedSummary | null;
  loading: boolean;
  showBaseCurrency?: boolean;
};

/** USD always; base currency first when a single region contributed. */
const MoneyValue: FC<{ value: AggregatedMoney; showBaseCurrency?: boolean }> = ({ value, showBaseCurrency }) => {
  const [t] = useTranslation();
  const partialMark = value.incomplete && value.amount_usd !== null ? ' *' : '';
  const partialTitle = value.incomplete ? t('DD_USD_INCOMPLETE') : undefined;
  if (showBaseCurrency && value.amount !== undefined && value.currency && value.currency !== 'USD') {
    return (
      <>
        <KpiValue>{formatMoney(value.amount, value.currency)}</KpiValue>
        <KpiSecondary title={partialTitle}>≈ {formatMoney(value.amount_usd, 'USD')}{partialMark}</KpiSecondary>
      </>
    );
  }
  return (
    <KpiValue title={partialTitle}>
      {formatMoney(value.amount_usd, 'USD')}
      {partialMark}
    </KpiValue>
  );
};

const DeltaBadge: FC<{ current: string | number | null; previous: string | number | null; goodWhenUp?: boolean }> = ({
  current,
  previous,
  goodWhenUp = true,
}) => {
  const [t] = useTranslation();
  const pct = percentDelta(current, previous);
  if (pct === null) return <Delta $direction="flat">{t('DD_NO_PREV')}</Delta>;
  const direction = Math.abs(pct) < 0.05 ? 'flat' : pct > 0 ? 'up' : 'down';
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '■';
  return (
    <Delta $direction={direction} $goodWhenUp={goodWhenUp}>
      {arrow} {formatPercent(Math.abs(pct))}
    </Delta>
  );
};

const KpiTiles: FC<Props> = ({ summary, loading, showBaseCurrency }) => {
  const [t] = useTranslation();

  if (loading) {
    return (
      <KpiGrid>
        {Array.from({ length: 7 }, (_, i) => (
          <KpiCard key={i}>
            <Skeleton width="60%" />
            <Skeleton height={26} />
            <Skeleton width="40%" />
          </KpiCard>
        ))}
      </KpiGrid>
    );
  }

  if (!summary) return <Card><Empty>{t('DD_NO_DATA')}</Empty></Card>;
  const s = summary;
  const usd = (v: AggregatedMoney) => `${formatMoney(v.amount_usd, 'USD')}${v.incomplete && v.amount_usd !== null ? ' *' : ''}`;

  return (
    <KpiGrid>
      <KpiCard>
        <KpiLabel>{t('DD_KPI_MRR')}</KpiLabel>
        <MoneyValue showBaseCurrency={showBaseCurrency} value={s.mrr} />
        <KpiSecondary>
          <DeltaBadge current={s.mrr.incomplete ? null : s.mrr.amount_usd} previous={s.mrr_prev.incomplete ? null : s.mrr_prev.amount_usd} />{' '}
          {t('DD_KPI_MRR_HINT', { month: s.mrr.month ? formatMonth(s.mrr.month) : '—' })}
        </KpiSecondary>
        <KpiSecondary>
          {t('DD_KPI_CURRENT_MONTH', { value: usd(s.current_month_estimate) })}
        </KpiSecondary>
      </KpiCard>

      <KpiCard>
        <KpiLabel>{t('DD_KPI_CASH_IN')}</KpiLabel>
        <MoneyValue showBaseCurrency={showBaseCurrency} value={s.cash_in_30d} />
        <KpiSecondary>
          <DeltaBadge current={s.cash_in_30d.incomplete ? null : s.cash_in_30d.amount_usd} previous={s.cash_in_prev_30d.incomplete ? null : s.cash_in_prev_30d.amount_usd} />{' '}
          {t('DD_KPI_CASH_IN_HINT')}
        </KpiSecondary>
        <KpiSecondary>
          {t('DD_KPI_CASH_SPLIT', {
            gateway: usd(s.cash_in_30d_breakdown.gateway),
            external: usd(s.cash_in_30d_breakdown.external),
          })}
        </KpiSecondary>
      </KpiCard>

      <KpiCard $tone="warning">
        <KpiLabel>
          {t('DD_KPI_STAFF_CREDITS')}
          <InfoDot
            role="img"
            tabIndex={0}
            aria-label={t('DD_KPI_STAFF_CREDITS_TOOLTIP')}
            title={t('DD_KPI_STAFF_CREDITS_TOOLTIP')}
          >
            !
          </InfoDot>
        </KpiLabel>
        <MoneyValue showBaseCurrency={showBaseCurrency} value={s.staff_credits_30d.net} />
        <KpiSecondary>
          {t('DD_KPI_STAFF_CREDITS_HINT', {
            operations: formatInt(s.staff_credits_30d.operations),
            clients: formatInt(s.staff_credits_30d.clients),
          })}
        </KpiSecondary>
      </KpiCard>

      <KpiCard>
        <KpiLabel>{t('DD_KPI_PAYING')}</KpiLabel>
        <KpiValue>{formatInt(s.paying_clients.count)}</KpiValue>
        <KpiSecondary>
          <DeltaBadge current={s.paying_clients.count} previous={s.paying_clients_prev.count} />{' '}
          {t('DD_KPI_PAYING_HINT', { month: s.paying_clients.month ? formatMonth(s.paying_clients.month) : '—' })}
        </KpiSecondary>
      </KpiCard>

      <KpiCard>
        <KpiLabel>{t('DD_KPI_NEW')}</KpiLabel>
        <KpiValue>{formatInt(s.new_clients_30d)}</KpiValue>
        <KpiSecondary>{t('DD_KPI_NEW_PAYING', { count: s.new_paying_clients_30d })}</KpiSecondary>
      </KpiCard>

      <KpiCard>
        <KpiLabel>{t('DD_KPI_CHURN')}</KpiLabel>
        <KpiValue>{formatInt(s.churned_clients.count)}</KpiValue>
        <KpiSecondary>
          {t('DD_KPI_CHURN_HINT', {
            month: s.churned_clients.month ? formatMonth(s.churned_clients.month) : '—',
          })}
        </KpiSecondary>
      </KpiCard>

      <KpiCard>
        <KpiLabel>{t('DD_KPI_RECEIVABLES')}</KpiLabel>
        <MoneyValue showBaseCurrency={showBaseCurrency} value={s.receivables} />
        <KpiSecondary>
          {t('DD_KPI_RECEIVABLES_HINT', {
            invoices: formatInt(s.receivables.unpaid_invoices),
            clients: formatInt(s.receivables.negative_balance_clients),
          })}
        </KpiSecondary>
      </KpiCard>
    </KpiGrid>
  );
};

export default KpiTiles;
